/**
 * Settings -> Handheld updates
 *
 * Publishes JS bundles and APKs to the Zebra RFID handheld from any browser.
 * Until this page existed, shipping a fix needed one specific PC; the gun is
 * now in an American warehouse and the developer is not.
 *
 * Two things this page exists to get right:
 *
 *  - "Now serving" comes first, because the only question anyone actually has
 *    is "what is the gun running right now".
 *  - runtimeVersion is explained in words and checked against the channel's
 *    APK before a publish goes out. A wrong value there does not fail loudly;
 *    it leaves a device 12,000 km away asking for a bundle nothing can run.
 *
 * Access: the API decides. GET /rfid-publish/state answers 403 for anyone not
 * on the allow-list, and we render nothing but a notice -- never a page whose
 * every button is going to fail.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import authHeader from "services/auth-header";
import API from "services/axios";
import {
  CHANNELS,
  currentRuntimeVersion,
  formatWhen,
  isForbidden,
  isRuntimeMismatch,
  serverError,
  shortId,
} from "./rfidPublishUtils";

const RUNTIME_HELP =
  "runtimeVersion ties a JS bundle to a native build. A handheld only downloads a bundle whose runtimeVersion matches the APK it is running -- type the wrong one and the device keeps the old bundle forever, with no error anyone can see from here. Use the runtimeVersion of the APK that channel is on (shown above).";

const emptyBundleForm = {
  channel: "production",
  runtimeVersion: "",
  notes: "",
};

const emptyApkForm = {
  channel: "production",
  runtimeVersion: "",
  versionCode: "",
  versionName: "",
  notes: "",
};

function NowServingCard({ channel, state }) {
  const js = state?.js;
  const apk = state?.apk;
  return (
    <div className="col-12 col-lg-6 mb-3">
      <div className="card h-100 shadow-sm custom-border-radius-sm">
        <div className="card-header bg-white d-flex align-items-center justify-content-between">
          <h6 className="mb-0 text-capitalize">
            <i className="mdi mdi-cellphone-wireless me-1" />
            {channel}
          </h6>
          <span
            className={`badge ${
              channel === "production" ? "bg-success" : "bg-secondary"
            }`}
          >
            {currentRuntimeVersion(state)
              ? `runtime ${currentRuntimeVersion(state)}`
              : "no runtime yet"}
          </span>
        </div>
        <div className="card-body">
          <div className="mb-3">
            <div className="fw-bold small text-uppercase text-muted">
              JS bundle
            </div>
            {js ? (
              <div data-testid={`js-${channel}`}>
                <div>
                  {/* A rollback row has no updateId, and rendering its absence
                      as a bare dash hides the single most important state this
                      card can be in: an emergency stop is in force and every
                      device is back on the JS inside its APK. */}
                  {js.kind === "rollback" ? (
                    <span className="text-danger fw-bold">
                      Rolled back — devices run the JS inside their APK
                    </span>
                  ) : (
                    <code>{shortId(js.updateId)}</code>
                  )}
                  <span className="text-muted"> · runtime </span>
                  <strong>{js.runtimeVersion || "-"}</strong>
                </div>
                <div className="small text-muted">
                  {formatWhen(js.publishedAt)}
                  {js.publishedBy ? ` · ${js.publishedBy}` : ""}
                </div>
                {js.notes ? <div className="small">{js.notes}</div> : null}
              </div>
            ) : (
              <div className="text-muted" data-testid={`js-${channel}`}>
                Nothing published — devices run the JS baked into their APK.
              </div>
            )}
          </div>
          <div>
            <div className="fw-bold small text-uppercase text-muted">
              Newest APK
            </div>
            {apk ? (
              <div data-testid={`apk-${channel}`}>
                <div>
                  <strong>{apk.versionName || "-"}</strong>
                  <span className="text-muted"> (code {apk.versionCode})</span>
                  <span className="text-muted"> · runtime </span>
                  <strong>{apk.runtimeVersion || "-"}</strong>
                </div>
                <div className="small text-muted">
                  {formatWhen(apk.publishedAt)}
                  {apk.publishedBy ? ` · ${apk.publishedBy}` : ""}
                </div>
                {apk.notes ? <div className="small">{apk.notes}</div> : null}
              </div>
            ) : (
              <div className="text-muted" data-testid={`apk-${channel}`}>
                No APK recorded for this channel.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ProgressBar({ percent }) {
  return (
    <div className="progress mt-2" style={{ height: 18 }}>
      <div
        className="progress-bar progress-bar-striped progress-bar-animated"
        role="progressbar"
        style={{ width: `${percent}%` }}
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        {percent}%
      </div>
    </div>
  );
}

export default function HandheldUpdates() {
  const navigate = useNavigate();

  /*   All States
   ********************************************* */
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [stateError, setStateError] = useState("");
  const [publishState, setPublishState] = useState(null);

  const [history, setHistory] = useState([]);
  const [historyError, setHistoryError] = useState("");

  const [bundleForm, setBundleForm] = useState(emptyBundleForm);
  const [bundleFile, setBundleFile] = useState(null);
  const [bundleBusy, setBundleBusy] = useState(false);
  const [bundleProgress, setBundleProgress] = useState(0);
  const [bundleError, setBundleError] = useState("");
  const [bundleOk, setBundleOk] = useState("");
  const [bundleConfirmMismatch, setBundleConfirmMismatch] = useState(false);

  const [apkForm, setApkForm] = useState(emptyApkForm);
  const [apkFile, setApkFile] = useState(null);
  const [apkBusy, setApkBusy] = useState(false);
  const [apkProgress, setApkProgress] = useState(0);
  const [apkError, setApkError] = useState("");
  const [apkOk, setApkOk] = useState("");

  const [rollbackForm, setRollbackForm] = useState({
    channel: "production",
    runtimeVersion: "",
    mode: "embedded",
  });
  const [rollbackConfirm, setRollbackConfirm] = useState(false);
  const [rollbackBusy, setRollbackBusy] = useState(false);
  const [rollbackError, setRollbackError] = useState("");
  const [rollbackOk, setRollbackOk] = useState("");

  // Pre-fill runtimeVersion from "Now serving" only until the operator types
  // their own value -- re-filling under their fingers is how typos get made.
  const bundleTouched = useRef(false);
  const apkTouched = useRef(false);
  const rollbackTouched = useRef(false);

  /*   All Functions
   ********************************************* */
  const fetchHistory = useCallback(async () => {
    try {
      const { data } = await API.get("/rfid-publish/history?limit=25", {
        headers: authHeader(),
      });
      setHistory(Array.isArray(data) ? data : data?.data || []);
      setHistoryError("");
    } catch (error) {
      setHistoryError(serverError(error, "Could not load publish history"));
    }
  }, []);

  const fetchState = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await API.get("/rfid-publish/state", {
        headers: authHeader(),
      });
      // The API wraps payloads as {success, data}. Accept the bare object too,
      // so the page does not silently render empty "Now serving" cards if that
      // envelope ever changes -- which is exactly how this bug was found.
      setPublishState(data?.data ?? data);
      setForbidden(false);
      setStateError("");
      return data;
    } catch (error) {
      if (isForbidden(error)) {
        setForbidden(true);
        setPublishState(null);
      } else {
        setStateError(serverError(error, "Could not load handheld state"));
      }
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const refresh = useCallback(async () => {
    const data = await fetchState();
    if (data) await fetchHistory();
  }, [fetchState, fetchHistory]);

  const channelState = useCallback(
    (channel) => publishState?.channels?.[channel] || null,
    [publishState]
  );

  const bundleChannelState = channelState(bundleForm.channel);
  const apkChannelState = channelState(apkForm.channel);

  const bundleMismatch = useMemo(
    () => isRuntimeMismatch(bundleForm.runtimeVersion, bundleChannelState),
    [bundleForm.runtimeVersion, bundleChannelState]
  );
  const apkMismatch = useMemo(
    () => isRuntimeMismatch(apkForm.runtimeVersion, apkChannelState),
    [apkForm.runtimeVersion, apkChannelState]
  );

  // A changed mismatch has to be re-acknowledged; an old "yes" must not carry.
  useEffect(() => {
    setBundleConfirmMismatch(false);
  }, [bundleForm.runtimeVersion, bundleForm.channel]);

  const publishBundle = async () => {
    setBundleError("");
    setBundleOk("");
    if (!bundleFile) {
      setBundleError("Choose the .zip of the expo export output first.");
      return;
    }
    if (!bundleForm.runtimeVersion?.trim()) {
      setBundleError("runtimeVersion is required.");
      return;
    }
    if (bundleMismatch && !bundleConfirmMismatch) {
      setBundleError(
        `runtimeVersion "${bundleForm.runtimeVersion}" does not match the ${bundleForm.channel} APK ("${bundleChannelState?.apk?.runtimeVersion}"). Tick the box below if you really mean it.`
      );
      return;
    }

    const body = new FormData();
    body.append("file", bundleFile);
    body.append("channel", bundleForm.channel);
    body.append("runtimeVersion", bundleForm.runtimeVersion.trim());
    body.append("notes", bundleForm.notes || "");

    try {
      setBundleBusy(true);
      setBundleProgress(0);
      await API.post("/rfid-publish/bundle", body, {
        headers: { ...authHeader(), "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          const total = e.total || e.loaded || 1;
          setBundleProgress(Math.round((e.loaded * 100) / total));
        },
      });
      setBundleOk("Bundle published. Devices pick it up on their next check.");
      setBundleFile(null);
      setBundleForm((prev) => ({ ...prev, notes: "" }));
      await refresh();
    } catch (error) {
      setBundleError(serverError(error, "Publish failed"));
    } finally {
      setBundleBusy(false);
    }
  };

  const publishApk = async () => {
    setApkError("");
    setApkOk("");
    if (!apkFile) {
      setApkError("Choose the .apk file first.");
      return;
    }
    if (!apkForm.runtimeVersion?.trim()) {
      setApkError("runtimeVersion is required.");
      return;
    }
    if (!apkForm.versionCode?.toString().trim()) {
      setApkError("versionCode is required.");
      return;
    }
    if (!apkForm.versionName?.trim()) {
      setApkError("versionName is required.");
      return;
    }

    const body = new FormData();
    body.append("file", apkFile);
    body.append("channel", apkForm.channel);
    body.append("runtimeVersion", apkForm.runtimeVersion.trim());
    body.append("versionCode", apkForm.versionCode.toString().trim());
    body.append("versionName", apkForm.versionName.trim());
    body.append("notes", apkForm.notes || "");

    try {
      setApkBusy(true);
      setApkProgress(0);
      await API.post("/rfid-publish/apk", body, {
        headers: { ...authHeader(), "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          const total = e.total || e.loaded || 1;
          setApkProgress(Math.round((e.loaded * 100) / total));
        },
      });
      setApkOk("APK published. It has to be installed on the device by hand.");
      setApkFile(null);
      setApkForm((prev) => ({ ...prev, notes: "" }));
      await refresh();
    } catch (error) {
      setApkError(serverError(error, "Publish failed"));
    } finally {
      setApkBusy(false);
    }
  };

  const doRollback = async () => {
    setRollbackError("");
    setRollbackOk("");
    if (!rollbackForm.runtimeVersion?.trim()) {
      setRollbackError("runtimeVersion is required.");
      setRollbackConfirm(false);
      return;
    }
    try {
      setRollbackBusy(true);
      await API.post(
        "/rfid-publish/rollback",
        {
          channel: rollbackForm.channel,
          runtimeVersion: rollbackForm.runtimeVersion.trim(),
          mode: rollbackForm.mode,
        },
        { headers: authHeader() }
      );
      setRollbackOk(
        rollbackForm.mode === "embedded"
          ? "Rolled back to embedded. Devices fall back to the JS inside their APK."
          : "Re-served the last good bundle."
      );
      setRollbackConfirm(false);
      await refresh();
    } catch (error) {
      setRollbackError(serverError(error, "Rollback failed"));
      setRollbackConfirm(false);
    } finally {
      setRollbackBusy(false);
    }
  };

  /*   All UseEffects
   ********************************************* */
  useEffect(() => {
    refresh();
  }, [refresh]);

  // Pre-fill the three runtimeVersion fields from what the channel serves.
  useEffect(() => {
    if (!publishState) return;
    if (!bundleTouched.current) {
      const rv = currentRuntimeVersion(channelState(bundleForm.channel));
      if (rv) setBundleForm((prev) => ({ ...prev, runtimeVersion: rv }));
    }
    if (!apkTouched.current) {
      const rv = currentRuntimeVersion(channelState(apkForm.channel));
      if (rv) setApkForm((prev) => ({ ...prev, runtimeVersion: rv }));
    }
    if (!rollbackTouched.current) {
      const rv = currentRuntimeVersion(channelState(rollbackForm.channel));
      if (rv) setRollbackForm((prev) => ({ ...prev, runtimeVersion: rv }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publishState, bundleForm.channel, apkForm.channel, rollbackForm.channel]);

  /*   Render
   ********************************************* */
  if (loading) {
    return (
      <div className="p-4" style={{ marginLeft: 285 }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // Not on the allow-list: show nothing that could be clicked.
  if (forbidden) {
    return (
      <div className="p-4" style={{ marginLeft: 285 }}>
        <div
          className="alert alert-secondary mt-5"
          style={{ maxWidth: 560 }}
          data-testid="handheld-forbidden"
        >
          <h5 className="mb-2">
            <i className="mdi mdi-lock-outline me-1" />
            Handheld updates
          </h5>
          <p className="mb-0">
            Your account is not allowed to publish handheld updates. Nothing to
            do here.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 pb-5" style={{ marginLeft: 285, minHeight: "90vh" }}>
      <div className="d-flex align-items-center justify-content-between mt-4 mb-3">
        <h2 className="m-0">
          <i className="mdi mdi-cellphone-arrow-down me-2" />
          Handheld updates
        </h2>
        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={() => refresh()}
          >
            <i className="ri-refresh-line me-1" />
            Refresh
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={() => navigate("/settings")}
          >
            Back to Settings
          </button>
        </div>
      </div>

      {stateError ? (
        <div className="alert alert-danger" data-testid="state-error">
          {stateError}
        </div>
      ) : null}

      {/*   Now serving
       ********************************************* */}
      <h5 className="mb-2">Now serving</h5>
      <div className="row">
        {CHANNELS.map((channel) => (
          <NowServingCard
            key={channel}
            channel={channel}
            state={channelState(channel)}
          />
        ))}
      </div>

      {/*   Publish a JS bundle
       ********************************************* */}
      <div className="card shadow-sm custom-border-radius-sm mb-3">
        <div className="card-header bg-white">
          <h6 className="mb-0">
            <i className="mdi mdi-language-javascript me-1" />
            Publish a JS bundle
          </h6>
        </div>
        <div className="card-body">
          <div className="row g-2">
            <div className="col-12 col-md-5">
              <label className="form-label small fw-bold" htmlFor="bundle-file">
                Bundle (.zip of the <code>expo export</code> output directory)
              </label>
              <input
                id="bundle-file"
                type="file"
                accept=".zip"
                className="form-control"
                disabled={bundleBusy}
                onChange={(e) => setBundleFile(e.target.files?.[0] || null)}
              />
            </div>
            <div className="col-6 col-md-3">
              <label
                className="form-label small fw-bold"
                htmlFor="bundle-channel"
              >
                Channel
              </label>
              <select
                id="bundle-channel"
                className="form-select"
                disabled={bundleBusy}
                value={bundleForm.channel}
                onChange={(e) => {
                  bundleTouched.current = false;
                  setBundleForm((prev) => ({
                    ...prev,
                    channel: e.target.value,
                  }));
                }}
              >
                {CHANNELS.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-6 col-md-4">
              <label
                className="form-label small fw-bold"
                htmlFor="bundle-runtime"
              >
                runtimeVersion
              </label>
              <input
                id="bundle-runtime"
                type="text"
                className={`form-control ${bundleMismatch ? "is-invalid" : ""}`}
                disabled={bundleBusy}
                value={bundleForm.runtimeVersion}
                onChange={(e) => {
                  bundleTouched.current = true;
                  setBundleForm((prev) => ({
                    ...prev,
                    runtimeVersion: e.target.value,
                  }));
                }}
              />
            </div>
            <div className="col-12">
              <div className="form-text">{RUNTIME_HELP}</div>
            </div>
            <div className="col-12">
              <label className="form-label small fw-bold" htmlFor="bundle-notes">
                Notes
              </label>
              <input
                id="bundle-notes"
                type="text"
                className="form-control"
                disabled={bundleBusy}
                placeholder="What changed, in one line"
                value={bundleForm.notes}
                onChange={(e) =>
                  setBundleForm((prev) => ({ ...prev, notes: e.target.value }))
                }
              />
            </div>
          </div>

          {bundleMismatch ? (
            <div className="alert alert-warning mt-3" data-testid="bundle-mismatch">
              <div className="fw-bold">
                <i className="mdi mdi-alert-outline me-1" />
                runtimeVersion does not match the {bundleForm.channel} APK
              </div>
              <div className="small">
                The {bundleForm.channel} APK is on{" "}
                <strong>{bundleChannelState?.apk?.runtimeVersion}</strong>; you
                typed <strong>{bundleForm.runtimeVersion}</strong>. A device on
                that APK will never download this bundle — it will sit on its
                current JS with no visible error.
              </div>
              <div className="form-check mt-2">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="bundle-mismatch-ack"
                  checked={bundleConfirmMismatch}
                  onChange={(e) => setBundleConfirmMismatch(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="bundle-mismatch-ack">
                  I know, publish it to this runtimeVersion anyway
                </label>
              </div>
            </div>
          ) : null}

          {bundleBusy ? <ProgressBar percent={bundleProgress} /> : null}
          {bundleError ? (
            <div className="alert alert-danger mt-3" data-testid="bundle-error">
              {bundleError}
            </div>
          ) : null}
          {bundleOk ? (
            <div className="alert alert-success mt-3">{bundleOk}</div>
          ) : null}

          <div className="d-flex align-items-center gap-3 mt-3">
            <button
              type="button"
              className="btn btn-primary"
              disabled={bundleBusy}
              onClick={publishBundle}
            >
              {bundleBusy ? "Publishing…" : "Publish bundle"}
            </button>
            <div className="small text-muted">
              A publish cannot be deleted. The only way back is{" "}
              <strong>Roll back</strong> below.
            </div>
          </div>
        </div>
      </div>

      {/*   Publish an APK
       ********************************************* */}
      <div className="card shadow-sm custom-border-radius-sm mb-3">
        <div className="card-header bg-white">
          <h6 className="mb-0">
            <i className="mdi mdi-android me-1" />
            Publish an APK
          </h6>
        </div>
        <div className="card-body">
          <div className="row g-2">
            <div className="col-12 col-md-5">
              <label className="form-label small fw-bold" htmlFor="apk-file">
                APK file
              </label>
              <input
                id="apk-file"
                type="file"
                accept=".apk"
                className="form-control"
                disabled={apkBusy}
                onChange={(e) => setApkFile(e.target.files?.[0] || null)}
              />
            </div>
            <div className="col-6 col-md-3">
              <label className="form-label small fw-bold" htmlFor="apk-channel">
                Channel
              </label>
              <select
                id="apk-channel"
                className="form-select"
                disabled={apkBusy}
                value={apkForm.channel}
                onChange={(e) => {
                  apkTouched.current = false;
                  setApkForm((prev) => ({ ...prev, channel: e.target.value }));
                }}
              >
                {CHANNELS.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-6 col-md-4">
              <label className="form-label small fw-bold" htmlFor="apk-runtime">
                runtimeVersion
              </label>
              <input
                id="apk-runtime"
                type="text"
                className="form-control"
                disabled={apkBusy}
                value={apkForm.runtimeVersion}
                onChange={(e) => {
                  apkTouched.current = true;
                  setApkForm((prev) => ({
                    ...prev,
                    runtimeVersion: e.target.value,
                  }));
                }}
              />
            </div>
            <div className="col-12">
              <div className="form-text">
                {RUNTIME_HELP} A new APK may legitimately introduce a new
                runtimeVersion — that is the one case where changing it is
                right, and every bundle after it must use the new value.
              </div>
            </div>
            <div className="col-6 col-md-3">
              <label className="form-label small fw-bold" htmlFor="apk-code">
                versionCode
              </label>
              <input
                id="apk-code"
                type="number"
                className="form-control"
                disabled={apkBusy}
                value={apkForm.versionCode}
                onChange={(e) =>
                  setApkForm((prev) => ({
                    ...prev,
                    versionCode: e.target.value,
                  }))
                }
              />
            </div>
            <div className="col-6 col-md-3">
              <label className="form-label small fw-bold" htmlFor="apk-name">
                versionName
              </label>
              <input
                id="apk-name"
                type="text"
                className="form-control"
                disabled={apkBusy}
                value={apkForm.versionName}
                onChange={(e) =>
                  setApkForm((prev) => ({
                    ...prev,
                    versionName: e.target.value,
                  }))
                }
              />
            </div>
            <div className="col-12 col-md-6">
              <label className="form-label small fw-bold" htmlFor="apk-notes">
                Notes
              </label>
              <input
                id="apk-notes"
                type="text"
                className="form-control"
                disabled={apkBusy}
                value={apkForm.notes}
                onChange={(e) =>
                  setApkForm((prev) => ({ ...prev, notes: e.target.value }))
                }
              />
            </div>
          </div>

          {apkMismatch ? (
            <div className="alert alert-warning mt-3" data-testid="apk-mismatch">
              <i className="mdi mdi-alert-outline me-1" />
              This APK declares runtimeVersion{" "}
              <strong>{apkForm.runtimeVersion}</strong>, not{" "}
              <strong>{apkChannelState?.apk?.runtimeVersion}</strong> like the
              current {apkForm.channel} APK. That is correct only if the native
              build really changed; after this, bundles for {apkForm.channel}{" "}
              must use the new value or installed devices stop updating.
            </div>
          ) : null}

          {apkBusy ? <ProgressBar percent={apkProgress} /> : null}
          {apkError ? (
            <div className="alert alert-danger mt-3" data-testid="apk-error">
              {apkError}
            </div>
          ) : null}
          {apkOk ? <div className="alert alert-success mt-3">{apkOk}</div> : null}

          <div className="d-flex align-items-center gap-3 mt-3">
            <button
              type="button"
              className="btn btn-primary"
              disabled={apkBusy}
              onClick={publishApk}
            >
              {apkBusy ? "Publishing…" : "Publish APK"}
            </button>
            <div className="small text-muted">
              A publish cannot be deleted — publish a newer build instead.
            </div>
          </div>
        </div>
      </div>

      {/*   Roll back
       ********************************************* */}
      <div className="card shadow-sm custom-border-radius-sm mb-3">
        <div className="card-header bg-white">
          <h6 className="mb-0">
            <i className="mdi mdi-backup-restore me-1" />
            Roll back
          </h6>
        </div>
        <div className="card-body">
          <div className="row g-2">
            <div className="col-6 col-md-3">
              <label
                className="form-label small fw-bold"
                htmlFor="rollback-channel"
              >
                Channel
              </label>
              <select
                id="rollback-channel"
                className="form-select"
                value={rollbackForm.channel}
                onChange={(e) => {
                  rollbackTouched.current = false;
                  setRollbackForm((prev) => ({
                    ...prev,
                    channel: e.target.value,
                  }));
                }}
              >
                {CHANNELS.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-6 col-md-3">
              <label
                className="form-label small fw-bold"
                htmlFor="rollback-runtime"
              >
                runtimeVersion
              </label>
              <input
                id="rollback-runtime"
                type="text"
                className="form-control"
                value={rollbackForm.runtimeVersion}
                onChange={(e) => {
                  rollbackTouched.current = true;
                  setRollbackForm((prev) => ({
                    ...prev,
                    runtimeVersion: e.target.value,
                  }));
                }}
              />
            </div>
            <div className="col-12 col-md-6">
              <label className="form-label small fw-bold" htmlFor="rollback-mode">
                Mode
              </label>
              <select
                id="rollback-mode"
                className="form-select"
                value={rollbackForm.mode}
                onChange={(e) =>
                  setRollbackForm((prev) => ({ ...prev, mode: e.target.value }))
                }
              >
                <option value="embedded">
                  embedded — devices fall back to the JS inside their APK
                </option>
                <option value="republish">
                  republish — re-serve the last good bundle
                </option>
              </select>
            </div>
          </div>

          {rollbackError ? (
            <div className="alert alert-danger mt-3" data-testid="rollback-error">
              {rollbackError}
            </div>
          ) : null}
          {rollbackOk ? (
            <div className="alert alert-success mt-3">{rollbackOk}</div>
          ) : null}

          <button
            type="button"
            className="btn btn-warning mt-3"
            disabled={rollbackBusy}
            onClick={() => {
              setRollbackError("");
              setRollbackOk("");
              setRollbackConfirm(true);
            }}
          >
            {rollbackBusy ? "Rolling back…" : "Roll back"}
          </button>
        </div>
      </div>

      {/*   History
       ********************************************* */}
      <div className="card shadow-sm custom-border-radius-sm mb-4">
        <div className="card-header bg-white">
          <h6 className="mb-0">
            <i className="mdi mdi-history me-1" />
            History
          </h6>
        </div>
        <div className="card-body">
          {historyError ? (
            <div className="alert alert-warning" data-testid="history-error">
              {historyError}
            </div>
          ) : null}
          <div style={{ maxHeight: "45vh", overflowY: "auto" }}>
            <table className="table table-sm">
              <thead>
                <tr className="sticky-top bg-white">
                  <th>Kind</th>
                  <th>Channel</th>
                  <th>runtimeVersion</th>
                  <th>Version / update</th>
                  <th>Notes</th>
                  <th>By</th>
                  <th>When</th>
                </tr>
              </thead>
              <tbody>
                {history?.length ? (
                  history.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <span
                          className={`badge ${
                            row.kind === "rollback"
                              ? "bg-warning text-dark"
                              : row.kind === "apk"
                                ? "bg-dark"
                                : "bg-primary"
                          }`}
                        >
                          {row.kind}
                        </span>
                      </td>
                      <td className="text-capitalize">{row.channel}</td>
                      <td>{row.runtimeVersion || "-"}</td>
                      <td>
                        {row.kind === "apk"
                          ? `${row.versionName || "-"} (${row.versionCode ?? "-"})`
                          : shortId(row.updateId)}
                      </td>
                      <td>{row.notes || "-"}</td>
                      <td>{row.publishedBy || "-"}</td>
                      <td>{formatWhen(row.publishedAt)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="text-center text-muted">
                      Nothing published yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/*   Rollback confirm
       ********************************************* */}
      <Modal
        show={rollbackConfirm}
        centered
        onHide={() => setRollbackConfirm(false)}
      >
        <Modal.Body className="bg-white px-3 py-3 custom-border-radius">
          <h5 className="mb-3">
            <i className="mdi mdi-alert-outline text-warning me-1" />
            Roll back {rollbackForm.channel}?
          </h5>
          {rollbackForm.mode === "embedded" ? (
            <p>
              Every handheld on channel{" "}
              <strong>{rollbackForm.channel}</strong> at runtimeVersion{" "}
              <strong>{rollbackForm.runtimeVersion || "(blank)"}</strong> will
              stop receiving the published JS bundle and fall back to the
              JavaScript baked into the APK it has installed. Anything shipped
              as a JS-only fix since that APK was built is gone from those
              devices until you publish again.
            </p>
          ) : (
            <p>
              The last good bundle for channel{" "}
              <strong>{rollbackForm.channel}</strong> at runtimeVersion{" "}
              <strong>{rollbackForm.runtimeVersion || "(blank)"}</strong> will
              be served again. Handhelds pick it up on their next update check;
              the bundle published after it stops being served.
            </p>
          )}
          <p className="text-muted small mb-3">
            The device is in the warehouse and nobody here can touch it. Make
            sure the channel and runtimeVersion above are the ones you mean.
          </p>
          <div className="d-flex justify-content-end gap-2">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setRollbackConfirm(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-warning"
              disabled={rollbackBusy}
              onClick={doRollback}
            >
              Yes, roll back
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}
